import axios, {AxiosRequestConfig, AxiosResponse, AxiosError} from "axios"
import {get} from "lodash"

import setIfNotSet from "../../setIfNotSet"
import refreshAccessToken from "./utils/refreshToken"
import is401, {isGraph401} from "./utils/refreshToken/is401"
import mfaToken, {isMFARequired, isGraphMFARequired} from "./utils/mfa"
import addGenericHeaders from "./utils/addHeaders"
import genericParseResponse from "./utils/parseResponse"
import removeAuthHeader from "./utils/removeAuthHeader"
import checkForMaintenance from "./utils/checkForMaintenance"
import {stripEtag} from "./utils/stripEtag"
import genericErrorHandler from "./utils/errorHandler"
import errorHandlerRegister, {ErrorHandlerCallback} from "./utils/errorHandler/register"

export interface APIRequestConfig extends AxiosRequestConfig {
  unAuth?: boolean
  etag?: string
  isApolloQuery?: boolean
  isGraphQuery?: boolean
  isReplay?: boolean
  useErrorHandler?: boolean
  addCSRF?: boolean
  sentryIgnoreError?: string[]
  sentryIgnoreAllErrors?: boolean
  ignoreMaintenance?: boolean
  /** required to set the correct headers for things like csrf in graphql */
  isFileUpload?: boolean
  /** Android specifically will use native fetch, so we pass this through the api calls */
  isAndroidFileUpload?: boolean
}

export interface APIResponse extends AxiosResponse {
  config: APIRequestConfig
}

class API {
  setTokens(accessToken: string) {
    _accessToken = accessToken
  }

  forceTokenRefresh() {
    return refreshTokenAndReplay()
  }

  clearTokens() {
    _accessToken = undefined
  }

  hasTokens() {
    return !!_accessToken
  }

  getToken() {
    return _accessToken
  }

  request(config?: APIRequestConfig) {
    // For android file uploads, use native fetch instead of axios
    if (config?.isAndroidFileUpload) {
      return this.requestWithNativeFetch(config)
    }

    return axios.request(addHeaders(config)).catch(errorHandler).then(parseResponse)
  }

  async requestWithNativeFetch(config: APIRequestConfig = {}) {
    // Apply headers (including auth token) - this is the key part we need
    const options = addHeaders(config)

    // Convert axios config to fetch options
    const fetchOptions = {
      method: options.method?.toUpperCase() || "GET",
      headers: options.headers as Record<string, string>,
      body: options.data
    }

    try {
      const response = await fetch(options.url, fetchOptions)

      if (!response || typeof response.text !== "function") {
        throw new Error(`Invalid response object: ${typeof response}`)
      }

      // Convert fetch response to axios-like response
      const data = await response.text()
      let parsedData
      try {
        parsedData = JSON.parse(data)
      } catch {
        parsedData = data
      }

      const axiosLikeResponse: APIResponse = {
        data: parsedData,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        config: options,
        request: {} // placeholder
      }

      // Handle 401s manually for native fetch (avoid axios replay logic)
      if (response.status === 401 && !options.unAuth) {
        return this.handleNativeFetch401(options, axiosLikeResponse)
      }

      // Apply the same response processing as axios (but skip 401 handling)
      return parseResponse(axiosLikeResponse)
    } catch (error) {
      // Convert fetch error to axios-like error
      const axiosLikeError = {
        config: options,
        response: undefined,
        request: {},
        message: error.message
      }

      return errorHandler(axiosLikeError as any)
    }
  }

  async handleNativeFetch401(originalConfig: APIRequestConfig, response: APIResponse): Promise<APIResponse> {
    await refreshAccessToken(response, _accessToken)

    // Update the token in headers and retry with native fetch
    const retryConfig = {...originalConfig}
    retryConfig.headers.Authorization = _accessToken

    return await this.requestWithNativeFetch(retryConfig)
  }

  get(url: string, config?: APIRequestConfig): Promise<APIResponse> {
    return axios.get(url, addHeaders(config)).catch(errorHandler).then(parseResponse)
  }

  post(url: string, data?: any, config?: APIRequestConfig): Promise<APIResponse> {
    return axios.post(url, data, addHeaders(config)).catch(errorHandler).then(parseResponse)
  }

  put(url: string, data: any, etag?: string, config?: APIRequestConfig): Promise<APIResponse> {
    return axios.put(url, stripEtag(data), addHeaders(config, etag)).catch(errorHandler).then(parseResponse)
  }

  patch(url: string, data: any, etag: string, config?: APIRequestConfig): Promise<APIResponse> {
    return axios.patch(url, stripEtag(data), addHeaders(config, etag)).catch(errorHandler).then(parseResponse)
  }

  head(url: string, config?: APIRequestConfig): Promise<APIResponse> {
    return axios.head(url, config).catch(errorHandler)
  }

  delete(url: string, etag: string, config?: APIRequestConfig): Promise<APIResponse> {
    return axios.delete(url, addHeaders(config, etag)).catch(errorHandler)
  }

  sentryIgnoreError(config: APIRequestConfig, arr: string[]) {
    setIfNotSet(config, "sentryIgnoreError", arr)
  }

  sentryIgnoreAllErrors(config: APIRequestConfig, boolean = true) {
    setIfNotSet(config, "sentryIgnoreAllErrors", boolean)
  }

  registerErrorHandlers(handlers: ErrorHandlerCallback[]) {
    handlers.forEach((handler) => errorHandlerRegister.add(handler))
  }
}

export default new API()

/**
 * @description: Privately scoping accessToken + refreshToken so they cannot be programaitcally accessed.
 */
let _accessToken: string | undefined

function addHeaders(config?: APIRequestConfig, etag?: string) {
  const options = addGenericHeaders(config, etag)
  if (!options.headers.Authorization && !options.unAuth && _accessToken) {
    options.headers.Authorization = _accessToken
  }

  return options
}

function parseResponse(res: APIResponse) {
  // apollo's axios-fetch accepts every status, so a 503 maintenance response arrives here instead of errorHandler
  checkForMaintenance({config: res.config, response: res} as AxiosError)
  if (isGraphMFARequired(res)) {
    return mfaTokenAndReplay(res)
  }
  if (isGraph401(res)) {
    return refreshTokenAndReplay(res)
  }
  genericParseResponse(res)
  return res
}

function errorHandler(err: AxiosError) {
  const useErrorHandler = get(err, "config.useErrorHandler", true)
  const unAuth = get(err, "config.unAuth", false)
  if (isMFARequired(err)) {
    return mfaTokenAndReplay(err)
  }
  if (useErrorHandler && !unAuth && is401(err)) {
    return refreshTokenAndReplay(err)
  }
  removeAuthHeader(err.config)
  checkForMaintenance(err)
  return genericErrorHandler(err)
}

function refreshTokenAndReplay(err?: AxiosError | APIResponse): Promise<any> {
  const replayConfig = err?.config
  return refreshAccessToken(err, _accessToken).then(() => {
    if (replayConfig) {
      replayConfig.headers.Authorization = _accessToken
      return replay(replayConfig)
    }
  })
}

function mfaTokenAndReplay(err: AxiosError | APIResponse): Promise<any> {
  const replayConfig = err.config
  return mfaToken(err).then(() => {
    replayConfig.headers.Authorization = _accessToken
    return replay(replayConfig)
  })
}

// The first replay is parsed again so that an expired token followed by an MFA
// requirement (or vice versa) still triggers the second flow. A replay of a replay
// is sent raw, which bounds the chain and avoids loops on a persistent 401/MFA.
function replay(config: APIRequestConfig) {
  if (config.isReplay) {
    return axios.request(config)
  }
  return axios
    .request({...config, isReplay: true})
    .catch(errorHandler)
    .then(parseResponse)
}
